import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import { PowerPointIcon, UploadIcon, TrashIcon } from "../../../../icons";
// @ts-ignore - Lottie animation
import dotsAnimation from "../../../../../assets/dots.json";
const ChooseStyleGuide = ({ onNext, onSelect, initialValue, currentPlanId, }) => {
    // Real presentations from DeckRegistry
    const [presentations, setPresentations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    /**
     * Check if a title is a valid/meaningful presentation name
     * Invalid titles include: "Untitled", "Untitled Presentation", empty, placeholder values,
     * or tracker-generated files like "PowerPoint add-in {uuid}.pptx"
     */
    const isValidTitle = (title) => {
        if (!title)
            return false;
        const lowerTitle = title.toLowerCase().trim();
        // Invalid if it's just "untitled" variants or contains "property" (like "Title property in win32")
        const invalidPatterns = [
            'untitled',
            'untitled presentation',
            'presentation',
            'property',
        ];
        // Check for basic invalid patterns
        if (invalidPatterns.some(pattern => lowerTitle === pattern || lowerTitle.includes('property'))) {
            return false;
        }
        // Filter out tracker-generated files: "PowerPoint add-in {uuid}.pptx"
        // Pattern: starts with "powerpoint add-in" followed by a UUID-like string
        if (lowerTitle.startsWith('powerpoint add-in')) {
            return false;
        }
        return true;
    };
    // Fetch tracked decks on mount
    useEffect(() => {
        const fetchDecks = async () => {
            setIsLoading(true);
            try {
                if (window.__getTrackedDecks) {
                    const decks = await window.__getTrackedDecks();
                    console.log('[ChooseStyleGuide] Fetched tracked decks:', decks);
                    // Filter and transform DeckInfo to Presentation format
                    // - Show current presentation even if it has an invalid/untitled name
                    // - Only show other presentations if they have valid titles
                    const transformedDecks = decks
                        .filter((deck) => {
                        // Always show the current presentation
                        if (deck.isCurrent) {
                            console.log('[ChooseStyleGuide] Including current presentation:', deck.title);
                            return true;
                        }
                        // For other presentations, only show if they have a valid title
                        const hasValidTitle = isValidTitle(deck.title);
                        if (!hasValidTitle) {
                            console.log('[ChooseStyleGuide] Filtering out presentation with invalid title:', deck.title);
                        }
                        return hasValidTitle;
                    })
                        .map((deck, index) => ({
                        id: String(index),
                        documentId: deck.id,
                        name: deck.title.endsWith('.pptx') ? deck.title : `${deck.title}.pptx`,
                        isCurrent: deck.isCurrent,
                    }));
                    console.log('[ChooseStyleGuide] Filtered presentations:', transformedDecks.length, 'of', decks.length);
                    setPresentations(transformedDecks);
                }
                else {
                    console.warn('[ChooseStyleGuide] __getTrackedDecks not available on window');
                    setPresentations([]);
                }
            }
            catch (error) {
                console.error('[ChooseStyleGuide] Error fetching decks:', error);
                setPresentations([]);
            }
            finally {
                setIsLoading(false);
            }
        };
        fetchDecks();
    }, []);
    // Find if initialValue matches a presentation
    const matchedPresentation = presentations.find(p => p.documentId === initialValue?.documentId);
    const isInitialValueUploadedFile = initialValue && initialValue.type === 'uploaded' && !matchedPresentation;
    const [selectedPresentation, setSelectedPresentation] = useState(matchedPresentation ? matchedPresentation.id : null);
    const [uploadedFile, setUploadedFile] = useState(isInitialValueUploadedFile ? { name: initialValue.label, path: initialValue.path } : null);
    const [isUploadedFileSelected, setIsUploadedFileSelected] = useState(!!isInitialValueUploadedFile);
    const [uploadError, setUploadError] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const uploadInputRef = useRef(null);
    const handlePresentationSelect = (presentation) => {
        setUploadError(null);
        setSelectedPresentation(presentation.id);
        setIsUploadedFileSelected(false); // Deselect uploaded file
        const styleGuide = {
            type: 'live',
            documentId: presentation.documentId,
            label: presentation.name,
        };
        onSelect(styleGuide);
        // Auto-advance to next step
        onNext();
    };
    const uploadSelectedFile = async (file) => {
        if (!file.name.toLowerCase().endsWith(".pptx")) {
            setUploadError("Only .pptx files are supported.");
            return;
        }
        if (!currentPlanId) {
            setUploadError("Plan is still initializing. Please wait a moment and try again.");
            return;
        }
        if (!window.__plansApi_uploadStyleGuide) {
            setUploadError("Style-guide upload service is unavailable.");
            return;
        }
        setUploadError(null);
        setIsUploading(true);
        try {
            const result = await window.__plansApi_uploadStyleGuide(currentPlanId, file);
            if (!result?.style_guide_presentation_path) {
                throw new Error("Upload did not return a style-guide path.");
            }
            const tempdocumentId = `uploaded_${Date.now()}`;
            const uploadedName = result.filename || file.name;
            setUploadedFile({ name: uploadedName, path: result.style_guide_presentation_path });
            setIsUploadedFileSelected(true);
            setSelectedPresentation(null);
            const styleGuide = {
                type: 'uploaded',
                documentId: tempdocumentId,
                label: uploadedName,
                path: result.style_guide_presentation_path,
            };
            onSelect(styleGuide);
            onNext();
        }
        catch (error) {
            console.error('[ChooseStyleGuide] Error uploading style guide:', error);
            setUploadError(error instanceof Error ? error.message : "Failed to upload style guide.");
        }
        finally {
            setIsUploading(false);
        }
    };
    const handleUploadFileClick = () => {
        uploadInputRef.current?.click();
    };
    const handleUploadFileChange = async (event) => {
        const selectedFile = event.target.files?.[0];
        event.target.value = "";
        if (!selectedFile) {
            return;
        }
        await uploadSelectedFile(selectedFile);
    };
    const handleUploadedFileClick = () => {
        if (uploadedFile) {
            setUploadError(null);
            setIsUploadedFileSelected(true);
            setSelectedPresentation(null);
            // Re-select the uploaded file
            const tempdocumentId = `uploaded_${Date.now()}`;
            const styleGuide = {
                type: 'uploaded',
                documentId: tempdocumentId,
                label: uploadedFile.name,
                path: uploadedFile.path,
            };
            onSelect(styleGuide);
            // Auto-advance to next step
            onNext();
        }
    };
    const handleRemoveUploadedFile = (e) => {
        e.stopPropagation();
        setUploadError(null);
        setUploadedFile(null);
        setIsUploadedFileSelected(false);
        // If a presentation is selected, keep that selection
        if (selectedPresentation) {
            const selectedPres = presentations.find(p => p.id === selectedPresentation);
            if (selectedPres) {
                const styleGuide = {
                    type: 'live',
                    documentId: selectedPres.documentId,
                    label: selectedPres.name,
                };
                onSelect(styleGuide);
            }
        }
    };
    return (_jsxs("div", { className: "flex flex-col gap-6", children: [_jsxs("div", { className: "flex flex-col gap-2", children: [_jsx("h2", { className: "text-[19px] font-bold leading-tight text-gray-990", children: "Choose styling from an existing deck" }), _jsx("p", { className: "text-[13px] text-gray-600", children: "Pick a presentation and Carousel will use it as a reference for layout, typography and visual inspiration. You can make changes to this later." })] }), _jsxs("div", { className: "flex flex-col items-center justify-center p-8 border border-dashed rounded-lg transition-colors border-primary-purple-40 bg-[rgba(248,247,255,0.90)]", children: [_jsx("input", { ref: uploadInputRef, type: "file", accept: ".pptx", className: "hidden", onChange: handleUploadFileChange }), _jsxs("button", { type: "button", onClick: handleUploadFileClick, disabled: isUploading, className: "flex pt-2 px-3 pb-3 flex-col items-center rounded-lg border border-primary-purple-15 bg-primary-purple-10 cursor-pointer hover:bg-primary-purple-15 transition-colors disabled:opacity-60 disabled:cursor-not-allowed", children: [_jsxs("div", { className: "flex items-center gap-2 text-blue-600 hover:text-blue-700", children: [_jsx(UploadIcon, { size: 16, color: "currentColor" }), _jsx("span", { className: "text-sm font-medium", children: isUploading ? "Uploading style guide..." : "Click here to select file" })] }), _jsx("span", { className: "text-xs text-gray-500 mt-1", children: ".pptx" })] })] }), uploadError && (_jsx("p", { className: "text-[12px] text-red-600 -mt-3", children: uploadError })), uploadedFile && (_jsxs("div", { onClick: handleUploadedFileClick, className: `flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${isUploadedFileSelected
                    ? "border-primary-purple-30 bg-primary-purple-5 shadow"
                    : "border-gray-200 bg-white hover:bg-gray-50"}`, children: [_jsx(PowerPointIcon, { variant: "large" }), _jsxs("span", { className: "flex-1 text-[13px] text-gray-990 leading-4", children: ["[New] ", uploadedFile.name] }), _jsx("button", { onClick: (e) => handleRemoveUploadedFile(e), "aria-label": "Remove file", className: "shrink-0 p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer", children: _jsx(TrashIcon, { size: 16, color: "#6B7280" }) })] })), _jsxs("div", { className: "flex flex-col gap-3", children: [_jsx("h3", { className: "text-[13px] font-bold text-gray-990", children: "Recent Presentations" }), _jsx("div", { className: "flex flex-col gap-2", children: isLoading ? (_jsx("div", { className: "flex items-center justify-center py-6", children: _jsxs("div", { className: "flex items-center gap-1 text-gray-500 text-[13px]", children: ["Loading presentations", _jsx("lottie-player", { src: JSON.stringify(dotsAnimation), background: "transparent", speed: "1", style: { width: '24px', height: '24px' }, loop: true, autoplay: true })] }) })) : presentations.length === 0 ? (_jsx("div", { className: "flex items-center justify-center py-6 text-gray-500 text-[13px]", children: "No recent presentations found. Open a presentation in PowerPoint to use it as a style guide." })) : (presentations.map((presentation) => (_jsxs("div", { onClick: () => handlePresentationSelect(presentation), className: `flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedPresentation === presentation.id
                                ? "border-primary-purple-30 bg-primary-purple-5 shadow"
                                : "border-gray-200 bg-white hover:bg-gray-50"}`, children: [_jsx(PowerPointIcon, { variant: "large", size: 24 }), _jsx("span", { className: "flex-1 text-[13px] text-gray-990 leading-4", children: presentation.name }), presentation.isCurrent && (_jsx("span", { className: "px-2 py-1 text-xs font-medium text-blue-70 bg-[#EFF6FF] rounded border border-[#BFDBFE]", children: "Current Presentation" }))] }, presentation.id)))) })] })] }));
};
export default ChooseStyleGuide;
